import axios from 'axios';
import { REST_BASE } from './applicationTemplates';
import type { AgreementType, MessageType, ReportItemType } from '../helpers/types';

/**
 * Klient mot lyfti-api `/rest/application` — søkerens dashboard, delt i tre
 * uavhengige kall slik at søknadslista, fristene og de uleste meldingene
 * lastes hver for seg.
 *
 * Erstatter det ene `getApplicationsByUser`-kallet, som hentet alt i én runde:
 * hele payloaden, alle vedlegg, alle avtaler og alle rapporter på hver søknad.
 * Målt på lokal base tok det 12 s for seks søknader med elleve avtaler.
 *
 * Auth for søkere er `?sub={bankid}` + hostname → entity, som i
 * `applicationTemplates.ts`.
 */

/** Mager søknadsrad — kun feltene dashboard-kortene rendrer. */
export type ApplicationListItemType = {
    id: number;
    name: string | null;
    status: number;
    state: number;
    type: number;
    type_name: string | null;
    applicant_name: string | null;
    createdon: string;
    editedon: string | null;
    archivedon: string | null;
    award: {
        status: number | null;
        submittedon: string | null;
    } | null;
};

export type DeadlinesResponse = {
    reports: ReportItemType[];
    agreements: AgreementType[];
};

/**
 * Hostname REST-endepunktet skal resolve entity fra. Entity-velgeren lagrer et
 * overstyrt vertsnavn i localStorage; ellers er det nettleserens eget.
 */
export function resolveHostname(): string {
    return localStorage.getItem('lyfti_selected_entity') || window.location.hostname;
}

async function get<T>(path: string, sub: string): Promise<T | null> {
    if (!REST_BASE) {
        console.warn('[applications] VITE_REST_URL/VITE_API_URL mangler');
        return null;
    }
    if (!sub) {
        console.warn('[applications] OIDC sub mangler — kan ikke kalle REST-endepunktet');
        return null;
    }

    const url = `${REST_BASE}/application${path}`;
    try {
        const res = await axios.get(url, {
            headers: { Accept: 'application/json' },
            params: { sub, hostname: resolveHostname() },
        });
        return (res.data?.data ?? null) as T | null;
    } catch (error) {
        console.warn(`[applications] Kunne ikke hente ${url}:`, error);
        return null;
    }
}

/** Søknader og kladder. `null` betyr at kallet feilet. */
export function fetchApplications(sub: string): Promise<ApplicationListItemType[] | null> {
    return get<ApplicationListItemType[]>('', sub);
}

/**
 * Frister: rapporter som ligger til utfylling og avtaler som venter på
 * søkerens signatur. Filtreringen skjer server-side — det som kommer tilbake
 * skal vises som det er.
 */
export function fetchDeadlines(sub: string): Promise<DeadlinesResponse | null> {
    return get<DeadlinesResponse>('/deadlines', sub);
}

/** Siste uleste melding fra stiftelsen, per søknad. */
export function fetchUnreadMessages(sub: string): Promise<MessageType[] | null> {
    return get<MessageType[]>('/messages', sub);
}
